import * as XLSX from 'xlsx-js-style';
import i18n from '../../i18n';
import { getBrandCurrencySuffix, getBrandLocale } from './brandCurrency';
import {
  resolveReportExcelFilename,
  truncateSheetName,
} from './exportFilename';

const isMonetaryColumnHeader = (header) => {
  const h = String(header || '');
  const keywords = [
    i18n.t('common.price'),
    i18n.t('common.total'),
    i18n.t('reports.amount_ht'),
    i18n.t('reports.amount_ttc'),
    i18n.t('reports.tva'),
    i18n.t('reports.total_amount_ht'),
    i18n.t('reports.unit_price_ht'),
  ];
  return keywords.some((kw) => kw && h.includes(kw));
};

const isQuantityColumnHeader = (header) => {
  const h = String(header || '');
  return [i18n.t('reports.quantity_sold')].some(
    (kw) => kw && h.includes(kw)
  );
};

const parseCurrencyCellValue = (cell, currencySuffix) => {
  if (typeof cell !== 'string' || !cell.includes(currencySuffix)) {
    return cell;
  }
  const match = cell.match(/[\d\s,\.]+/);
  if (match) {
    const numValue = parseFloat(match[0].replace(/\s/g, '').replace(',', '.'));
    return isNaN(numValue) ? cell : numValue;
  }
  return cell;
};

// Professional grayscale Excel generator with clean styling using xlsx-js-style
export const generateReportExcel = async (reportType, reportTitle, settings) => {
  try {
    console.log('🚀 Starting PROFESSIONAL GRAYSCALE Excel generation for', reportType);
    
    // Create a new workbook
    const workbook = XLSX.utils.book_new();
    
    // Get company information
    const company = settings?.company || {};
    const companyName = company.name || company.companyName || 'Qawaem';
    const companyNameArabic = company.nameArabic || company.secondName || '';
    const companyActivity = company.activity || company.mainActivity || '';
    const companyServices = company.services || company.description || '';
    
    // Legal information (Saudi ZATCA identifiers with legacy fallbacks)
    const resolveCompanyField = (obj, primary, fallbacks = []) => {
      const primaryValue = obj?.[primary];
      if (primaryValue) return primaryValue;
      for (const key of fallbacks) {
        if (obj?.[key]) return obj[key];
      }
      return '';
    };
    const crNo = resolveCompanyField(company, 'commercialRegistration', ['rc', 'rcNo']);
    const unifiedNo = resolveCompanyField(company, 'unifiedNationalNumber', ['nis', 'nisNo', 'articleNumber', 'articleNo', 'article']);
    const vatNo = resolveCompanyField(company, 'vatNumber', ['nif', 'nifNo']);
    const gosiNo = resolveCompanyField(company, 'gosiEstablishmentNumber', ['gosiNumber', 'gosi']);
    const chamberNo = company.chamberOfCommerceNumber || '';
    const municipalityNo = company.municipalityLicenseNumber || '';
    const zakatNo = company.zakatNumber || '';
    
    // Contact information
    const address = company.address || '';
    const city = company.city || company.wilaya || '';
    const postalCode = company.postalCode || company.codePostal || '';
    const phone = company.phone || company.telephone || '';
    const email = company.email || '';
    const website = company.website || company.siteWeb || '';

    const labelAddress = i18n.t('common.address');
    const labelPhone = i18n.t('common.phone');
    const labelEmail = i18n.t('common.email');
    const labelWebsite = i18n.t('common.website');
    const labelLegalSection = i18n.t('common.legal_fiscal_section');
    const labelContactSection = i18n.t('common.contact_info');
    const labelNoData = i18n.t('common.no_data');
    const labelCr = i18n.t('common.commercial_registration');
    const labelUnifiedNo = i18n.t('common.unified_national_number');
    const labelVat = i18n.t('common.vat_number');
    const labelGosi = i18n.t('common.gosi_number');
    const labelChamber = i18n.t('common.chamber_number');
    const labelMunicipality = i18n.t('common.municipality_license');
    const labelZakat = i18n.t('common.zakat_number');
    const labelTotal = i18n.t('reports.total');
    const currencySuffix = getBrandCurrencySuffix();
    const excelCurrencyFormat = `#,##0.00" ${currencySuffix}"`;
    const legalFieldLabels = [labelCr, labelUnifiedNo, labelVat, labelGosi, labelChamber, labelMunicipality, labelZakat];

    // Create data array for the worksheet
    const wsData = [];
    let currentRow = 0;

    // 🎨 1. COMPANY HEADER SECTION - ULTRA STYLED
    // Company Name - Large, bold, with rich background - EXPLICITLY CREATE MERGED ROW
    const companyNameRow = [companyName];
    while (companyNameRow.length < 8) {
      companyNameRow.push('');
    }
    wsData.push(companyNameRow);
    currentRow++;

    // Arabic name if available - EXPLICITLY CREATE MERGED ROW
    if (companyNameArabic) {
      // Create array with enough empty cells for merging
      const arabicRow = [companyNameArabic];
      while (arabicRow.length < 8) {
        arabicRow.push('');
      }
      wsData.push(arabicRow);
      currentRow++;
    }

    // Activity - EXPLICITLY CREATE MERGED ROW
    if (companyActivity) {
      // Create array with enough empty cells for merging
      const activityRow = [companyActivity];
      while (activityRow.length < 8) {
        activityRow.push('');
      }
      wsData.push(activityRow);
      currentRow++;
    }

    // Services - EXPLICITLY CREATE MERGED ROW
    if (companyServices) {
      // Create array with enough empty cells for merging
      const servicesRow = [companyServices];
      while (servicesRow.length < 8) {
        servicesRow.push('');
      }
      wsData.push(servicesRow);
      currentRow++;
    }

         // Reduced spacing - smaller empty row
     wsData.push(['']);
     currentRow++;

    // 🔵 2. LEGAL INFORMATION SECTION
    if (crNo || unifiedNo || vatNo || gosiNo || chamberNo || municipalityNo || zakatNo) {
      // Section header - EXPLICITLY CREATE MERGED ROW
      const legalHeaderRow = [labelLegalSection];
      while (legalHeaderRow.length < 8) {
        legalHeaderRow.push('');
      }
      wsData.push(legalHeaderRow);
      currentRow++;

      // Legal details
      const legalInfo = [
        { label: labelCr, value: crNo },
        { label: labelUnifiedNo, value: unifiedNo },
        { label: labelVat, value: vatNo },
        { label: labelGosi, value: gosiNo },
        { label: labelChamber, value: chamberNo },
        { label: labelMunicipality, value: municipalityNo },
        { label: labelZakat, value: zakatNo }
      ];

             legalInfo.forEach(info => {
         if (info.value) {
           wsData.push([`${info.label}: ${info.value}`]);
           currentRow++;
         }
       });

       // Minimal spacing
       currentRow++;
    }

    // 🟢 3. CONTACT INFORMATION SECTION
    if (address || phone || email || website) {
      // Section header - EXPLICITLY CREATE MERGED ROW
      const contactHeaderRow = [labelContactSection];
      while (contactHeaderRow.length < 8) {
        contactHeaderRow.push('');
      }
      wsData.push(contactHeaderRow);
      currentRow++;

      // Contact details
      const contactInfo = [];
      if (address || city || postalCode) {
        const addressLine = [address, city, postalCode].filter(Boolean).join(', ');
        contactInfo.push({ label: labelAddress, value: addressLine });
      }
      if (phone) contactInfo.push({ label: labelPhone, value: phone });
      if (email) contactInfo.push({ label: labelEmail, value: email });
      if (website) contactInfo.push({ label: labelWebsite, value: website });

             contactInfo.forEach(info => {
         wsData.push([`${info.label}: ${info.value}`]);
         currentRow++;
       });

       // Minimal spacing
       currentRow++;
    }

    // 🔴 4. REPORT TITLE SECTION
    const today = new Date().toLocaleDateString(getBrandLocale());
    const fullReportTitle = `${reportTitle} - ${today}`;
    
    // Report title - EXPLICITLY CREATE MERGED ROW
    const reportTitleRow = [fullReportTitle];
    while (reportTitleRow.length < 8) {
      reportTitleRow.push('');
    }
         wsData.push(reportTitleRow);
     currentRow++;

     // Minimal spacing
     currentRow++;

    // 📊 5. DATA TABLE SECTION
    let reportElement = findReportTable(reportType);
    
    if (reportElement) {
      const tableData = extractTableDataFromElement(reportElement);
      
      if (tableData.headers.length > 0) {
        // Table headers - EXPLICITLY CREATE MERGED ROW
        const headerRow = [...tableData.headers];
        while (headerRow.length < 8) {
          headerRow.push('');
        }
        wsData.push(headerRow);
        currentRow++;

        // Table data rows
        tableData.rows.forEach(row => {
          const processedRow = row.map((cell, index) => {
            // Check if this is a TOTAL row
            const isTotalRow = row.some(c => c && c.toString().includes(labelTotal));
            
            if (isTotalRow) {
              // For TOTAL row, ensure numeric columns have values
              const header = tableData.headers[index] || '';
              if (index === 0) {
                return labelTotal;
              } else if (isMonetaryColumnHeader(header)) {
                // For monetary columns, return 0 if empty/undefined
                if (!cell || cell === '' || cell === null || cell === undefined) {
                  return 0;
                }
                const parsed = parseCurrencyCellValue(cell, currencySuffix);
                return typeof parsed === 'number' ? parsed : 0;
              } else {
                return cell || ''; // Non-monetary columns can be empty
              }
            } else {
              // Regular data row processing
              return parseCurrencyCellValue(cell, currencySuffix);
            }
          });
          wsData.push(processedRow);
          currentRow++;
        });

        // Add TOTAL row with default values if no data or if TOTAL row missing
        const hasTotalRow = tableData.rows.some(row => 
          row.some(cell => cell && cell.toString().includes(labelTotal))
        );
        
        if (!hasTotalRow || tableData.rows.length === 0) {
          // Create TOTAL row with appropriate values - EXPLICITLY CREATE MERGED ROW
          const totalRow = tableData.headers.map((header, index) => {
            if (index === 0) {
              return labelTotal;
            } else if (isMonetaryColumnHeader(header)) {
              return 0;
            } else {
              return ''; // Empty for non-numeric columns
            }
          });
          
          // Ensure row has at least 8 columns for merging
          while (totalRow.length < 8) {
            totalRow.push('');
          }
          
          wsData.push(totalRow);
          currentRow++;
        }
      }
    } else {
      // No data message - EXPLICITLY CREATE MERGED ROW
      const noDataRow = [labelNoData];
      while (noDataRow.length < 8) {
        noDataRow.push('');
      }
      wsData.push(noDataRow);
      currentRow++;
    }

    // Create worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(wsData);

    // 🎨 APPLY PROFESSIONAL GRAYSCALE STYLING
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    
    // Calculate maxCols early for styling and merging
    const actualDataCols = range.e.c + 1; // Actual data columns
    const maxCols = Math.max(8, actualDataCols); // Maximum for headers only
    
    // Style company header (first few rows)
    let styleRow = 0;
    
         // Company name styling - BLACK HEADER - APPLY TO ALL MERGED CELLS - COMPACT
     const companyNameStyle = {
       font: { sz: 14, bold: true, color: { rgb: "FFFFFF" } }, // Reduced from 20
       fill: { fgColor: { rgb: "2C2C2C" } }, // Dark gray, almost black
       border: {
         top: { style: "thick", color: { rgb: "000000" } },
         left: { style: "thick", color: { rgb: "000000" } },
         right: { style: "thick", color: { rgb: "000000" } },
         bottom: { style: "medium", color: { rgb: "000000" } }
       },
       alignment: { horizontal: "center", vertical: "center" }
     };
    
    // Apply style to all cells in the merged company name row
    for (let c = 0; c < maxCols; c++) {
      const cellRef = XLSX.utils.encode_cell({ r: styleRow, c });
      if (!worksheet[cellRef]) worksheet[cellRef] = { v: "" };
      
      // Use cleaner border style for columns beyond actual data
      let borderStyle = companyNameStyle;
      if (c >= actualDataCols) {
        borderStyle = {
          ...companyNameStyle,
          border: {
            top: { style: "thin", color: { rgb: "2C2C2C" } },
            left: { style: "thin", color: { rgb: "2C2C2C" } },
            right: { style: "thin", color: { rgb: "2C2C2C" } },
            bottom: { style: "thin", color: { rgb: "2C2C2C" } }
          }
        };
      }
      
      worksheet[cellRef].s = borderStyle;
    }
    styleRow++;

         // Arabic name styling - Light gray - APPLY TO ALL MERGED CELLS - COMPACT
     if (companyNameArabic) {
       const arabicStyle = {
         font: { sz: 10, italic: true, color: { rgb: "666666" } }, // Reduced from 12
         fill: { fgColor: { rgb: "F5F5F5" } }, // Very light gray
         border: {
           left: { style: "thick", color: { rgb: "000000" } },
           right: { style: "thick", color: { rgb: "000000" } },
           bottom: { style: "thin", color: { rgb: "CCCCCC" } }
         },
         alignment: { horizontal: "center", vertical: "center" }
       };
      
      // Apply style to all cells in the merged row
      for (let c = 0; c < maxCols; c++) {
        const cellRef = XLSX.utils.encode_cell({ r: styleRow, c });
        if (!worksheet[cellRef]) worksheet[cellRef] = { v: "" };
        
        // Use cleaner border style for columns beyond actual data
        let borderStyle = arabicStyle;
        if (c >= actualDataCols) {
          borderStyle = {
            ...arabicStyle,
            border: {
              left: { style: "thin", color: { rgb: "F5F5F5" } },
              right: { style: "thin", color: { rgb: "F5F5F5" } },
              bottom: { style: "thin", color: { rgb: "F5F5F5" } }
            }
          };
        }
        
        worksheet[cellRef].s = borderStyle;
      }
      styleRow++;
    }

         // Activity styling - Medium gray - APPLY TO ALL MERGED CELLS - COMPACT
     if (companyActivity) {
       const activityStyle = {
         font: { sz: 9, color: { rgb: "444444" } }, // Reduced from 11
         fill: { fgColor: { rgb: "F0F0F0" } }, // Light gray
         border: {
           left: { style: "thick", color: { rgb: "000000" } },
           right: { style: "thick", color: { rgb: "000000" } },
           bottom: { style: "thin", color: { rgb: "CCCCCC" } }
         },
         alignment: { horizontal: "center", vertical: "center", wrapText: true }
       };
      
      // Apply style to all cells in the merged row
      for (let c = 0; c < maxCols; c++) {
        const cellRef = XLSX.utils.encode_cell({ r: styleRow, c });
        if (!worksheet[cellRef]) worksheet[cellRef] = { v: "" };
        
        // Use cleaner border style for columns beyond actual data
        let borderStyle = activityStyle;
        if (c >= actualDataCols) {
          borderStyle = {
            ...activityStyle,
            border: {
              left: { style: "thin", color: { rgb: "F0F0F0" } },
              right: { style: "thin", color: { rgb: "F0F0F0" } },
              bottom: { style: "thin", color: { rgb: "F0F0F0" } }
            }
          };
        }
        
        worksheet[cellRef].s = borderStyle;
      }
      styleRow++;
    }

         // Services styling - Medium gray - APPLY TO ALL MERGED CELLS - COMPACT
     if (companyServices) {
       const servicesStyle = {
         font: { sz: 8, color: { rgb: "444444" } }, // Reduced from 10
         fill: { fgColor: { rgb: "F0F0F0" } }, // Light gray
         border: {
           left: { style: "thick", color: { rgb: "000000" } },
           right: { style: "thick", color: { rgb: "000000" } },
           bottom: { style: "thick", color: { rgb: "000000" } }
         },
         alignment: { horizontal: "center", vertical: "center", wrapText: true }
       };
      
      // Apply style to all cells in the merged row
      for (let c = 0; c < maxCols; c++) {
        const cellRef = XLSX.utils.encode_cell({ r: styleRow, c });
        if (!worksheet[cellRef]) worksheet[cellRef] = { v: "" };
        
        // Use cleaner border style for columns beyond actual data
        let borderStyle = servicesStyle;
        if (c >= actualDataCols) {
          borderStyle = {
            ...servicesStyle,
            border: {
              left: { style: "thin", color: { rgb: "F0F0F0" } },
              right: { style: "thin", color: { rgb: "F0F0F0" } },
              bottom: { style: "thin", color: { rgb: "F0F0F0" } }
            }
          };
        }
        
        worksheet[cellRef].s = borderStyle;
      }
      styleRow++;
    }

    // Skip empty rows and apply section styling
    for (let r = styleRow; r <= range.e.r; r++) {
      const cellRef = XLSX.utils.encode_cell({ r, c: 0 });
      if (worksheet[cellRef] && worksheet[cellRef].v) {
        const cellValue = worksheet[cellRef].v.toString();
        
                 // Legal section header - Dark gray background - APPLY TO ALL MERGED CELLS - COMPACT
         if (cellValue.includes(labelLegalSection)) {
           const legalHeaderStyle = {
             font: { sz: 10, bold: true, color: { rgb: "FFFFFF" } }, // Reduced from 12
             fill: { fgColor: { rgb: "555555" } }, // Medium dark gray
             border: {
               top: { style: "medium", color: { rgb: "000000" } },
               left: { style: "medium", color: { rgb: "000000" } },
               right: { style: "medium", color: { rgb: "000000" } },
               bottom: { style: "medium", color: { rgb: "000000" } }
             },
             alignment: { horizontal: "center", vertical: "center" }
           };
          
          // Apply to all cells in the merged row
          for (let c = 0; c < maxCols; c++) {
            const mergedCellRef = XLSX.utils.encode_cell({ r, c });
            if (!worksheet[mergedCellRef]) worksheet[mergedCellRef] = { v: "" };
            
            // Use cleaner border style for columns beyond actual data
            let borderStyle = legalHeaderStyle;
            if (c >= actualDataCols) {
              borderStyle = {
                ...legalHeaderStyle,
                border: {
                  top: { style: "thin", color: { rgb: "555555" } },
                  left: { style: "thin", color: { rgb: "555555" } },
                  right: { style: "thin", color: { rgb: "555555" } },
                  bottom: { style: "thin", color: { rgb: "555555" } }
                }
              };
            }
            
            worksheet[mergedCellRef].s = borderStyle;
          }
        }
        // Legal details - Light background
        else if (legalFieldLabels.some((label) => cellValue.startsWith(`${label}:`))) {
          worksheet[cellRef].s = {
            font: { sz: 10, bold: true, color: { rgb: "333333" } },
            fill: { fgColor: { rgb: "F8F8F8" } }, // Very light gray
            border: {
              top: { style: "thin", color: { rgb: "CCCCCC" } },
              left: { style: "thin", color: { rgb: "999999" } },
              right: { style: "thin", color: { rgb: "999999" } },
              bottom: { style: "thin", color: { rgb: "CCCCCC" } }
            },
            alignment: { horizontal: "left", vertical: "center" }
          };
        }
                 // Contact section header - Medium gray background - APPLY TO ALL MERGED CELLS - COMPACT
         else if (cellValue.includes(labelContactSection)) {
           const contactHeaderStyle = {
             font: { sz: 10, bold: true, color: { rgb: "FFFFFF" } }, // Reduced from 12
             fill: { fgColor: { rgb: "666666" } }, // Medium gray
             border: {
               top: { style: "medium", color: { rgb: "000000" } },
               left: { style: "medium", color: { rgb: "000000" } },
               right: { style: "medium", color: { rgb: "000000" } },
               bottom: { style: "medium", color: { rgb: "000000" } }
             },
             alignment: { horizontal: "center", vertical: "center" }
           };
          
          // Apply to all cells in the merged row
          for (let c = 0; c < maxCols; c++) {
            const mergedCellRef = XLSX.utils.encode_cell({ r, c });
            if (!worksheet[mergedCellRef]) worksheet[mergedCellRef] = { v: "" };
            
            // Use cleaner border style for columns beyond actual data
            let borderStyle = contactHeaderStyle;
            if (c >= actualDataCols) {
              borderStyle = {
                ...contactHeaderStyle,
                border: {
                  top: { style: "thin", color: { rgb: "666666" } },
                  left: { style: "thin", color: { rgb: "666666" } },
                  right: { style: "thin", color: { rgb: "666666" } },
                  bottom: { style: "thin", color: { rgb: "666666" } }
                }
              };
            }
            
            worksheet[mergedCellRef].s = borderStyle;
          }
        }
        // Contact details - Light background
        else if (cellValue.includes(`${labelAddress}:`) || cellValue.includes(`${labelPhone}:`)
                 || cellValue.includes(`${labelEmail}:`) || cellValue.includes(`${labelWebsite}:`)) {
          worksheet[cellRef].s = {
            font: { sz: 10, color: { rgb: "444444" } },
            fill: { fgColor: { rgb: "F8F8F8" } }, // Very light gray
            border: {
              top: { style: "thin", color: { rgb: "CCCCCC" } },
              left: { style: "thin", color: { rgb: "999999" } },
              right: { style: "thin", color: { rgb: "999999" } },
              bottom: { style: "thin", color: { rgb: "CCCCCC" } }
            },
            alignment: { horizontal: "left", vertical: "center" }
          };
        }
                 // Report title - BLACK background - APPLY TO ALL MERGED CELLS - COMPACT
         else if (cellValue.includes(reportTitle) && cellValue.includes(today)) {
           const reportTitleStyle = {
             font: { sz: 11, bold: true, color: { rgb: "FFFFFF" } }, // Reduced from 16
             fill: { fgColor: { rgb: "000000" } }, // Pure black
             border: {
               top: { style: "thick", color: { rgb: "000000" } },
               left: { style: "thick", color: { rgb: "000000" } },
               right: { style: "thick", color: { rgb: "000000" } },
               bottom: { style: "thick", color: { rgb: "000000" } }
             },
             alignment: { horizontal: "center", vertical: "center" }
           };
          
          // Apply to all cells in the merged row
          for (let c = 0; c < maxCols; c++) {
            const mergedCellRef = XLSX.utils.encode_cell({ r, c });
            if (!worksheet[mergedCellRef]) worksheet[mergedCellRef] = { v: "" };
            
            // Use cleaner border style for columns beyond actual data
            let borderStyle = reportTitleStyle;
            if (c >= actualDataCols) {
              borderStyle = {
                ...reportTitleStyle,
                border: {
                  top: { style: "thin", color: { rgb: "000000" } },
                  left: { style: "thin", color: { rgb: "000000" } },
                  right: { style: "thin", color: { rgb: "000000" } },
                  bottom: { style: "thin", color: { rgb: "000000" } }
                }
              };
            }
            
            worksheet[mergedCellRef].s = borderStyle;
          }
        }
      }
    }

    // Find and style the data table
    if (reportElement) {
      const tableData = extractTableDataFromElement(reportElement);
      if (tableData.headers.length > 0) {
        // Find table start row
        let tableStartRow = -1;
        for (let r = 0; r <= range.e.r; r++) {
          const cellRef = XLSX.utils.encode_cell({ r, c: 0 });
          if (worksheet[cellRef] && tableData.headers.includes(worksheet[cellRef].v)) {
            tableStartRow = r;
            break;
          }
        }

        if (tableStartRow >= 0) {
                     // Style table headers - BLACK HEADER - EXTEND TO FULL WIDTH - COMPACT
           const headerRowStyle = {
             font: { sz: 9, bold: true, color: { rgb: "FFFFFF" } }, // Reduced from 12
             fill: { fgColor: { rgb: "000000" } }, // Pure black
             border: {
               top: { style: "thick", color: { rgb: "000000" } },
               left: { style: "medium", color: { rgb: "000000" } },
               right: { style: "medium", color: { rgb: "000000" } },
               bottom: { style: "thick", color: { rgb: "000000" } }
             },
             alignment: { horizontal: "center", vertical: "center" }
           };
          
          // Apply to ALL columns (data + extra for full width)
          for (let c = 0; c < maxCols; c++) {
            const headerCellRef = XLSX.utils.encode_cell({ r: tableStartRow, c });
            if (!worksheet[headerCellRef]) worksheet[headerCellRef] = { v: "" };
            
            // Use cleaner border style for columns beyond actual data
            let borderStyle = headerRowStyle;
            if (c >= tableData.headers.length) {
              borderStyle = {
                ...headerRowStyle,
                border: {
                  top: { style: "thin", color: { rgb: "000000" } },
                  left: { style: "thin", color: { rgb: "000000" } },
                  right: { style: "thin", color: { rgb: "000000" } },
                  bottom: { style: "thin", color: { rgb: "000000" } }
                }
              };
            }
            
            worksheet[headerCellRef].s = borderStyle;
          }

          // Style table data rows - GRAYSCALE ONLY - EXTEND TOTAL AND NO DATA ROWS
          for (let r = tableStartRow + 1; r <= range.e.r; r++) {
            const firstCellRef = XLSX.utils.encode_cell({ r, c: 0 });
            if (worksheet[firstCellRef] && worksheet[firstCellRef].v) {
              const isEvenRow = (r - tableStartRow - 1) % 2 === 0;
              const isTotalRow = worksheet[firstCellRef].v.toString().includes(labelTotal);
              const isNoDataRow = worksheet[firstCellRef].v.toString().includes(labelNoData);

                             if (isTotalRow) {
                 // TOTAL row - EXTEND TO FULL WIDTH like headers - COMPACT
                 const totalRowStyle = {
                   font: { sz: 9, bold: true, color: { rgb: "FFFFFF" } }, // Reduced from 12
                   fill: { fgColor: { rgb: "333333" } }, // Dark gray
                   border: {
                     top: { style: "thick", color: { rgb: "000000" } },
                     left: { style: "thick", color: { rgb: "000000" } },
                     right: { style: "thick", color: { rgb: "000000" } },
                     bottom: { style: "thick", color: { rgb: "000000" } }
                   },
                   alignment: { horizontal: "center", vertical: "center" }
                 };

                // Apply to ALL columns for full width
                for (let c = 0; c < maxCols; c++) {
                  const cellRef = XLSX.utils.encode_cell({ r, c });
                  if (!worksheet[cellRef]) worksheet[cellRef] = { v: "" };
                  
                  // Use cleaner border style for columns beyond actual data
                  let borderStyle = totalRowStyle;
                  if (c >= tableData.headers.length) {
                    borderStyle = {
                      ...totalRowStyle,
                      border: {
                        top: { style: "thin", color: { rgb: "333333" } },
                        left: { style: "thin", color: { rgb: "333333" } },
                        right: { style: "thin", color: { rgb: "333333" } },
                        bottom: { style: "thin", color: { rgb: "333333" } }
                      }
                    };
                  }
                  
                  worksheet[cellRef].s = borderStyle;
                  
                  // Format numbers in TOTAL row (only for actual data columns)
                  if (c < tableData.headers.length && typeof worksheet[cellRef].v === 'number') {
                    worksheet[cellRef].z = excelCurrencyFormat;
                  }
                }
                             } else if (isNoDataRow) {
                 // No-data row - EXTEND TO FULL WIDTH - COMPACT
                 const noDataRowStyle = {
                   font: { sz: 8, italic: true, color: { rgb: "666666" } }, // Reduced from 10
                   fill: { fgColor: { rgb: "F8F8F8" } },
                   border: {
                     top: { style: "thin", color: { rgb: "CCCCCC" } },
                     left: { style: "thin", color: { rgb: "CCCCCC" } },
                     right: { style: "thin", color: { rgb: "CCCCCC" } },
                     bottom: { style: "thin", color: { rgb: "CCCCCC" } }
                   },
                   alignment: { horizontal: "center", vertical: "center" }
                 };

                // Apply to ALL columns for full width
                for (let c = 0; c < maxCols; c++) {
                  const cellRef = XLSX.utils.encode_cell({ r, c });
                  if (!worksheet[cellRef]) worksheet[cellRef] = { v: "" };
                  worksheet[cellRef].s = noDataRowStyle;
                }
              } else {
                                 // Regular data row - ONLY ACTUAL DATA COLUMNS - COMPACT
                 for (let c = 0; c < tableData.headers.length; c++) {
                   const cellRef = XLSX.utils.encode_cell({ r, c });
                   if (worksheet[cellRef]) {
                     worksheet[cellRef].s = {
                       font: { sz: 8, color: { rgb: "333333" } }, // Dark gray text - Reduced from 10
                       fill: { fgColor: { rgb: isEvenRow ? "F5F5F5" : "FFFFFF" } }, // Light gray vs white
                       border: {
                         top: { style: "thin", color: { rgb: "CCCCCC" } },
                         left: { style: "thin", color: { rgb: "CCCCCC" } },
                         right: { style: "thin", color: { rgb: "CCCCCC" } },
                         bottom: { style: "thin", color: { rgb: "CCCCCC" } }
                       },
                       alignment: { 
                         horizontal: typeof worksheet[cellRef].v === 'number' ? "right" : "left", 
                         vertical: "center" 
                       }
                     };
                    
                    // Format numbers with appropriate formatting
                    if (typeof worksheet[cellRef].v === 'number') {
                      const header = tableData.headers[c] || '';
                      if (isMonetaryColumnHeader(header)) {
                        worksheet[cellRef].z = excelCurrencyFormat;
                      } else if (header.includes('%') || header.includes(i18n.t('reports.percentage'))) {
                        worksheet[cellRef].z = '0.00%';
                      } else if (isQuantityColumnHeader(header)) {
                        worksheet[cellRef].z = '#,##0';
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }

    // Set optimized column widths - ONLY FOR ACTUAL DATA COLUMNS
    const colWidths = [];
    for (let i = 0; i < actualDataCols; i++) {
      let width = 12; // default smaller
      if (i === 0) width = 25; // First column much wider for labels/names
      if (i === 1) width = 15; // Second column
      if (i === 2) width = 15; // Third column  
      if (i === 3) width = 35; // Fourth column (descriptions/company names)
      if (i === 4) width = 20; // Fifth column (addresses)
      if (i === 5) width = 18; // Sixth column (amounts)
      if (i === 6) width = 15; // Seventh column
      if (i >= 7) width = 18; // Last columns for amounts
      colWidths.push({ wch: width });
    }
    worksheet['!cols'] = colWidths;

         // Set COMPACT row heights for single page printing
     if (!worksheet['!rows']) worksheet['!rows'] = [];
     
     // ALL ROWS - ULTRA COMPACT for single page
     for (let r = 0; r <= range.e.r; r++) {
       const cellRef = XLSX.utils.encode_cell({ r, c: 0 });
       if (worksheet[cellRef] && worksheet[cellRef].v) {
         const cellValue = worksheet[cellRef].v.toString();
         
         // Company name - slightly taller but still compact
         if (r === 0) {
           worksheet['!rows'][r] = { hpt: 18 }; // Reduced from 30
         }
         // Section headers - compact
         else if (cellValue.includes(labelLegalSection) || 
             cellValue.includes(labelContactSection) || 
             cellValue.includes(reportTitle) ||
             cellValue.includes(labelTotal) ||
             legalFieldLabels.some((label) => cellValue.startsWith(`${label}:`))) {
           worksheet['!rows'][r] = { hpt: 15 }; // Reduced from 25
         }
         // Data rows and everything else - minimal
         else {
           worksheet['!rows'][r] = { hpt: 12 }; // Very compact
         }
       } else {
         // Empty rows - minimal
         worksheet['!rows'][r] = { hpt: 8 };
       }
     }

    // Merge cells for better organization - EXTEND ALL HEADERS TO FULL WIDTH
    if (!worksheet['!merges']) worksheet['!merges'] = [];
    
    let currentMergeRow = 0;
    
    // Company name spans full width
    worksheet['!merges'].push({ s: { r: currentMergeRow, c: 0 }, e: { r: currentMergeRow, c: maxCols - 1 } });
    currentMergeRow++;
    
    // Arabic name if exists
    if (companyNameArabic) {
      worksheet['!merges'].push({ s: { r: currentMergeRow, c: 0 }, e: { r: currentMergeRow, c: maxCols - 1 } });
      currentMergeRow++;
    }
    
    // Activity spans full width
    if (companyActivity) {
      worksheet['!merges'].push({ s: { r: currentMergeRow, c: 0 }, e: { r: currentMergeRow, c: maxCols - 1 } });
      currentMergeRow++;
    }
    
    // Services spans full width
    if (companyServices) {
      worksheet['!merges'].push({ s: { r: currentMergeRow, c: 0 }, e: { r: currentMergeRow, c: maxCols - 1 } });
      currentMergeRow++;
    }
    
    // Get table data for merging logic
    const reportElementForMerging = findReportTable('');
    let tableHeaders = [];
    if (reportElementForMerging) {
      const tableDataForMerging = extractTableDataFromElement(reportElementForMerging);
      if (tableDataForMerging.headers.length > 0) {
        tableHeaders = tableDataForMerging.headers;
      }
    }
    
    // Find and merge ALL section headers, table elements, and special rows to full width
    for (let r = 0; r <= range.e.r; r++) {
      const cellRef = XLSX.utils.encode_cell({ r, c: 0 });
      if (worksheet[cellRef] && worksheet[cellRef].v) {
        const cellValue = worksheet[cellRef].v.toString();
        
        // Section headers span full width
        if (cellValue.includes(labelLegalSection) || 
            cellValue.includes(labelContactSection) || 
            cellValue.includes(reportTitle)) {
          worksheet['!merges'].push({ s: { r, c: 0 }, e: { r, c: maxCols - 1 } });
        }
        
        // Table headers span full width (like other headers) - check if it's the first header
        else if (tableHeaders.length > 0 && cellValue === tableHeaders[0]) {
          worksheet['!merges'].push({ s: { r, c: 0 }, e: { r, c: maxCols - 1 } });
        }
        
        // TOTAL row spans full width (like other headers)
        else if (cellValue.includes(labelTotal)) {
          worksheet['!merges'].push({ s: { r, c: 0 }, e: { r, c: maxCols - 1 } });
        }
        
        // No-data rows span full width
        else if (cellValue.includes(labelNoData)) {
          worksheet['!merges'].push({ s: { r, c: 0 }, e: { r, c: maxCols - 1 } });
        }
      }
    }

    // Add worksheet to workbook
    const sheetName = truncateSheetName(settings?.sheetName || reportTitle);
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // Generate filename and save
    const filename = resolveReportExcelFilename(reportType, reportTitle);
    
    // Write and download
    XLSX.writeFile(workbook, filename);
    
    console.log('🎉 PROFESSIONAL GRAYSCALE Excel file generated successfully:', filename);
    return { success: true, filename };
    
  } catch (error) {
    console.error('💥 Error generating PROFESSIONAL Excel:', error);
    throw error;
  }
};

// Helper function to find report table based on type
const findReportTable = (reportType) => {
  let selector = '';
  switch (reportType) {
    case 'etat104':
    case 'tva':
    case 'articles':
    case 'categories':
    case 'purchased-items':
    case 'payments':
    case 'finances':
    case 'stock-movements':
    case 'stock-lowstock':
    case 'stock-alerts':
    case 'returns':
      selector = '.report-content table';
      break;
    default:
      selector = '.report-content table';
  }
  
  return document.querySelector(selector);
};

// Helper function to extract data from table element
const extractTableDataFromElement = (tableElement) => {
  const currencySuffix = getBrandCurrencySuffix();
  const headers = [];
  const rows = [];
  
  // Get headers
  const headerRow = tableElement.querySelector('thead tr');
  if (headerRow) {
    headerRow.querySelectorAll('th').forEach(th => {
      headers.push(th.textContent.trim());
    });
  }
  
  // Get rows
  const bodyRows = tableElement.querySelectorAll('tbody tr');
  bodyRows.forEach(tr => {
    const rowData = [];
    tr.querySelectorAll('td').forEach(td => {
      let cellText = td.textContent.trim();
      
      // Handle different data types
      if (cellText.includes(currencySuffix)) {
        cellText = parseCurrencyCellValue(cellText, currencySuffix);
      } else if (cellText.includes('%')) {
        const percentMatch = cellText.match(/[\d,\.]+/);
        if (percentMatch) {
          const cleanPercent = percentMatch[0].replace(',', '.');
          if (!isNaN(cleanPercent)) {
            cellText = parseFloat(cleanPercent) / 100;
          }
        }
      } else if (/^\d+[\s,\.]*\d*$/.test(cellText.replace(/\s/g, ''))) {
        const cleanNumber = cellText.replace(/\s/g, '').replace(',', '.');
        if (!isNaN(cleanNumber)) {
          cellText = parseFloat(cleanNumber);
        }
      }
      
      rowData.push(cellText);
    });
    if (rowData.length > 0) {
      rows.push(rowData);
    }
  });
  
  return { headers, rows };
};
